<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  CategoryType,
  CategoryPayloadType,
} from "@/common/types/category";

import { baseInput, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: CategoryType | null;
  type: "CATEGORY" | "SUBCATEGORY" | "GROUP" | "SUBGROUP";
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  owner_id: props.data?.category_id,
  type: props.type,
  name: "",
  slug: "",
  description: "",
};

const payload = ref<CategoryPayloadType>({ ...DEFAULT_DATA });

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, slug, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    slug,
    description,
  };
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <article class="form-category">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('category.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("category.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.slug"
      :value="payload.slug"
      :placeholder="t('category.placeholders.slug')"
      :mask="maskCode"
      required
      @update:modelValue="debouceUpdate('slug')"
    >
      <template #label> {{ t("category.labels.slug") }} *</template>
    </base-input>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("category.labels.description") }}</template>
    </base-text-editor>
  </article>
</template>

<style lang="scss" scoped>
.form-category {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
